import { useEffect, useState } from "react";
import { Globe, Power, RefreshCw, Loader2, Check, X, AlertCircle, Inbox, Settings, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("channels");
  const [syncing, setSyncing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/ota/channels"), api.get("/ota/bookings")]);
      setChannels(a.data || []); setBookings(b.data || []);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleChannel = async (ch) => {
    try {
      await api.put(`/ota/channels/${ch.key}`, { enabled: !ch.enabled });
      toast.success(`${ch.name} ${!ch.enabled ? "attivato" : "disattivato"}`);
      load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const syncOne = async (key) => {
    setSyncing(key);
    try {
      const r = await api.post(`/ota/sync${key ? `?channel_key=${key}` : ""}`);
      const res = r.data;
      if (res.synced === 0) toast.info(res.message || "Nessun canale attivo");
      else toast.success(`Sync OK: ${res.synced} canale/i`);
      load();
    } catch (e) { toast.error("Errore sync: " + e.message); }
    finally { setSyncing(null); }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      if (draft.markup_pct != null) payload.markup_pct = +draft.markup_pct;
      if (draft.api_key) payload.api_key = draft.api_key;
      await api.put(`/ota/channels/${editing}`, payload);
      toast.success("Configurazione salvata");
      setEditing(null); setDraft({}); load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const seedDemo = async () => {
    try {
      const r = await api.post("/ota/bookings/seed-demo");
      toast.success(`${r.data.created} booking demo creati`);
      load();
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const confirmBooking = async (id) => {
    try {
      const r = await api.put(`/ota/bookings/${id}/confirm`);
      toast.success(`Contratto creato: ${r.data.contract_num}`);
      load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const rejectBooking = async (id) => {
    if (!confirm("Rifiutare booking OTA?")) return;
    await api.delete(`/ota/bookings/${id}`);
    toast.success("Booking rifiutato"); load();
  };

  const pendingCount = bookings.filter((b) => b.stato === "pending").length;
  const activeCount  = channels.filter((c) => c.enabled).length;

  return (
    <div className="space-y-6" data-testid="channels-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <Globe className="text-[#ff0033]" size={26} />
            Channel Manager OTA
          </h1>
          <p className="text-sm text-white/45 mt-1">Sincronizza disponibilità e prezzi con 7 broker globali · ricevi booking esterni · markup configurabile</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => syncOne(null)} disabled={syncing || activeCount === 0} data-testid="sync-all-btn"
            className="btn-neon px-5 py-2.5 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-40">
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Sync tutti i canali attivi ({activeCount})
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-white/10">
        <button onClick={() => setTab("channels")} data-testid="tab-channels"
          className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 ${tab === "channels" ? "text-white border-[#ff0033]" : "text-white/45 border-transparent hover:text-white"}`}>
          <Globe size={14} /> Canali ({channels.length})
        </button>
        <button onClick={() => setTab("inbox")} data-testid="tab-inbox"
          className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 ${tab === "inbox" ? "text-white border-[#ff0033]" : "text-white/45 border-transparent hover:text-white"}`}>
          <Inbox size={14} /> Inbox Booking ({pendingCount} pending)
        </button>
      </div>

      {loading && <div className="text-center py-12 text-white/40">Caricamento…</div>}

      {!loading && tab === "channels" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((ch) => (
            <div key={ch.key} data-testid={`channel-${ch.key}`}
              className={`glass rounded-2xl p-4 border-l-4 ${ch.enabled ? "border-emerald-500/60" : "border-white/10"} hover:scale-[1.01] transition`}>
              <div className="flex items-center gap-3 mb-3">
                <img src={`https://logo.clearbit.com/${ch.logo_domain}`} alt={ch.name}
                  className="w-12 h-12 bg-white rounded-lg p-1 object-contain"
                  onError={(e) => { e.target.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCI+PHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjMTExIi8+PHRleHQgeD0iMjQiIHk9IjI4IiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LXNpemU9IjEwIj48L3RleHQ+PC9zdmc+"; }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{ch.name}</div>
                  <div className="text-[10px] text-white/40 font-mono">{ch.logo_domain}</div>
                </div>
                <button onClick={() => toggleChannel(ch)} title={ch.enabled ? "Disattiva" : "Attiva"}
                  className={`p-2 rounded-lg ${ch.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-white/[0.04] text-white/40 hover:text-white"}`}
                  data-testid={`toggle-${ch.key}`}>
                  <Power size={16} />
                </button>
              </div>

              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-white/50">Markup</span> <span className="font-mono font-bold">{ch.markup_pct}%</span></div>
                <div className="flex justify-between"><span className="text-white/50">API key</span> <span className={ch.api_key_set ? "text-emerald-300" : "text-amber-300"}>{ch.api_key_set ? "✓ configurata" : "—"}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Ultimo sync</span> <span className="text-white/70">{ch.last_sync_at ? new Date(ch.last_sync_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</span></div>
                {ch.last_sync_ok != null && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Stato sync</span>
                    {ch.last_sync_ok ? <span className="text-emerald-300"><Check size={11} className="inline" /> OK</span> : <span className="text-rose-300"><AlertCircle size={11} className="inline" /> Errore</span>}
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-3">
                <button onClick={() => { setEditing(ch.key); setDraft({ markup_pct: ch.markup_pct }); }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] inline-flex items-center justify-center gap-1">
                  <Settings size={11} /> Configura
                </button>
                <button onClick={() => syncOne(ch.key)} disabled={!ch.enabled || syncing === ch.key}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-[#ff0033]/15 border border-[#ff0033]/40 text-[#ff5577] hover:bg-[#ff0033]/25 inline-flex items-center justify-center gap-1 disabled:opacity-30">
                  {syncing === ch.key ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Sync
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "inbox" && (
        <>
          {bookings.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center">
              <Inbox size={36} className="mx-auto text-white/20 mb-3" />
              <div className="text-white/55 mb-3">Nessun booking ricevuto dai canali OTA</div>
              <button onClick={seedDemo} data-testid="seed-demo-btn" className="btn-neon px-4 py-2 rounded-full text-xs">+ Crea 5 booking demo</button>
            </div>
          )}
          {bookings.length > 0 && (
            <div className="glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px] text-xs" data-testid="ota-bookings-table">
                  <thead className="bg-white/[0.04] text-white/55 uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="px-3 py-2 text-left">Canale</th>
                      <th className="px-2 py-2 text-left">Ext ID</th>
                      <th className="px-2 py-2 text-left">Cliente</th>
                      <th className="px-2 py-2 text-left">Veicolo</th>
                      <th className="px-2 py-2 text-left">Periodo</th>
                      <th className="px-2 py-2 text-right">Totale</th>
                      <th className="px-2 py-2 text-right">Netto</th>
                      <th className="px-2 py-2 text-center">Stato</th>
                      <th className="px-2 py-2 text-center">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id} className="border-t border-white/5">
                        <td className="px-3 py-2 text-[10px] uppercase tracking-wide font-bold">{b.channel}</td>
                        <td className="px-2 py-2 font-mono text-white/70">{b.external_id}</td>
                        <td className="px-2 py-2">
                          <div>{b.customer_name}</div>
                          <div className="text-[10px] text-white/40">{b.customer_city}</div>
                        </td>
                        <td className="px-2 py-2 text-white/80">{b.car_label}</td>
                        <td className="px-2 py-2 text-[10px]">
                          {b.data_inizio} → {b.data_fine}
                          <div className="text-[9px] text-white/40">{b.days} gg</div>
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-bold">€{b.totale.toLocaleString("it-IT")}</td>
                        <td className="px-2 py-2 text-right font-mono text-emerald-300">€{b.netto_locatore.toLocaleString("it-IT")}</td>
                        <td className="px-2 py-2 text-center">
                          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${
                            b.stato === "pending" ? "bg-amber-500/15 text-amber-300" :
                            b.stato === "confirmed" ? "bg-emerald-500/15 text-emerald-300" :
                            "bg-rose-500/15 text-rose-300"
                          }`}>{b.stato}</span>
                        </td>
                        <td className="px-2 py-2">
                          {b.stato === "pending" && (
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => confirmBooking(b.id)} data-testid={`confirm-${b.id}`}
                                className="px-2 py-1 rounded text-[10px] bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25">
                                <Check size={11} className="inline" /> Conferma
                              </button>
                              <button onClick={() => rejectBooking(b.id)}
                                className="px-2 py-1 rounded text-[10px] bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25">
                                <X size={11} className="inline" /> Rifiuta
                              </button>
                            </div>
                          )}
                          {b.stato === "confirmed" && <span className="text-[10px] text-emerald-300">✓ Contratto creato</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* EDIT MODAL */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md glass-strong rounded-3xl p-6">
            <h3 className="font-display text-xl font-bold mb-4">Configura canale</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Markup %</label>
                <input type="number" step="0.5" value={draft.markup_pct || 0}
                  onChange={(e) => setDraft({ ...draft, markup_pct: e.target.value })}
                  data-testid="markup-input"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
                <div className="text-[10px] text-white/40 mt-1">Maggiorazione applicata al prezzo base per questo canale</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">API Key (opzionale)</label>
                <input type="password" value={draft.api_key || ""}
                  onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                  placeholder="••••••••••"
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-2 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button onClick={saveEdit} data-testid="save-channel-btn" className="btn-neon px-5 py-2 rounded-full text-sm">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
